import { z } from "zod";

/**
 * School and work notes from a visit. The wording starts from the last note of the same kind, with
 * the client's name and dates as [placeholders] filled in for this client.
 */
export const EXCUSE_KINDS = ["school", "work"] as const;
export type ExcuseKind = (typeof EXCUSE_KINDS)[number];
export const EXCUSE_KIND_LABELS: Record<ExcuseKind, string> = { school: "School note", work: "Work note" };

/** Placeholders in a note's wording, and what each stands for. */
export const NOTE_PLACEHOLDERS = ["[Full name]", "[First name]", "[Visit date]", "[Return date]", "[Clinic]", "[Clinic phone]"] as const;
export type NoteValues = Record<(typeof NOTE_PLACEHOLDERS)[number], string>;

export const DEFAULT_NOTE_TEMPLATES: Record<ExcuseKind, string> = {
  school:
    "[Full name] was seen at [Clinic] on [Visit date]. Please excuse [First name] from school on [Visit date]. [First name] may return to school on [Return date].\n\nPlease contact our office at [Clinic phone] with any questions.",
  work: "[Full name] was seen at [Clinic] on [Visit date]. Please excuse [First name] from work on [Visit date]. [First name] may return to work on [Return date].\n\nPlease contact our office at [Clinic phone] with any questions.",
};

/** A note's wording for one client: placeholders filled in. */
export function fillNote(template: string, values: NoteValues): string {
  let out = template;
  for (const k of NOTE_PLACEHOLDERS) out = out.replaceAll(k, values[k]);
  return out;
}

/** The reverse: this client's name and dates back to placeholders, so the next note starts from the same wording. */
export function noteTemplate(body: string, values: NoteValues): string {
  let out = body;
  // Longest first, so "Ja'ron Jefferson" goes before "Ja'ron"; empty values are skipped.
  const keys = [...NOTE_PLACEHOLDERS].filter((k) => values[k].trim()).sort((a, b) => values[b].length - values[a].length);
  for (const k of keys) out = out.replaceAll(values[k], k);
  return out;
}

/** A date as it's written in a note: "September 10, 2026". */
export const noteDate = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** A PNG signature (drawn or uploaded), as a data URL. */
export const signatureSchema = z
  .string()
  .max(400_000, "That signature image is too big. Use a smaller one.")
  .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, "The signature must be a PNG image.");

// The return date is after the visit, so it never reads the same as the visit date (which would muddle the placeholders).
export const excuseNoteInputSchema = z.object({
  kind: z.enum(EXCUSE_KINDS),
  returnDate: isoDate,
  body: z.string().trim().min(1, "Write the note").max(4000),
  /** Only when the signer has no saved signature yet: sign here (and optionally keep it). */
  signature: signatureSchema.optional(),
  saveSignature: z.boolean().default(false),
});
export type ExcuseNoteInput = z.infer<typeof excuseNoteInputSchema>;

export const sendNoteSchema = z.object({
  channel: z.enum(["sms", "email"]),
  /** A text: which of the client's numbers; none = their mobile. */
  to: z.string().trim().max(30).nullish(),
});

/** What the note dialog starts from. */
export interface ExcuseNoteDraft {
  /** The last wording used for each kind, with placeholders. */
  templates: Record<ExcuseKind, string>;
  values: Omit<NoteValues, "[Return date]">;
  visitDate: string;
  defaultReturnDate: string;
  signer: { id: string; name: string; credentials: string; hasSignature: boolean };
}

export interface ExcuseNoteView {
  id: string;
  clientId: string;
  appointmentId: string | null;
  kind: ExcuseKind;
  visitDate: string;
  returnDate: string | null;
  body: string;
  signerName: string;
  signerCredentials: string;
  signedAt: string;
}

export const staffSignatureSchema = z.object({ signature: signatureSchema.nullable() });
